import { Component, computed, effect, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, signal, SimpleChanges, ViewChild, AfterViewInit } from '@angular/core';
import { GoldSearchCreatorResult } from '../../../creators/service/magic-search/magic-search.service';
import { interestsList } from '../../../../common/app-config/filter-models/interests';
import { ImageService } from '../../../../services/image/image.service';
import { DecimalPipe } from '@angular/common';
import { FormatService } from '../../../../common/format/format.service';
import { CreatorService } from '../../../../services/creator/creator.service';
import { differenceInDays, differenceInYears } from "date-fns";
import { MiscService } from '../../../../services/misc/misc.service';
import { CollaboratorService } from '../../../../services/collaborator/collaborator.service';
import { MagicSearchService } from '../../../creators/service/magic-search/magic-search.service';
import { VideoThumbnailComponent } from '../../../../atoms/video-thumbnail/video-thumbnail.component';
import { BriefApplicationResponseDto, VideoFormatEnum } from '../../../../common/general-types/briefs.dto';
import { BriefApplicationService } from '../../../../services/brief-application/brief-application.service';
import { ManageBriefTabsService } from '../../../manage-brief-tabs/service/manage-brief-tabs/manage-brief-tabs.service';
import { ApplicationStatusBadgeComponent } from '../../../../atoms/application-status-badge/application-status-badge.component';
import { LdgImgComponent } from '../../../../atoms/ldg-img/ldg-img.component';
import { VideoResponseDto } from '@/app/common/general-types/videos.dto';

const BIO_POPOVER_MAX_WIDTH_PX = Math.round(672 * 1.3 * 1.2);
@Component({
  selector: 'gsv4-gold-search-item',
  standalone: true,
  imports: [DecimalPipe, VideoThumbnailComponent, ApplicationStatusBadgeComponent, LdgImgComponent],
  templateUrl: './gold-search-item.component.html',
})
export class GoldSearchItemComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {

  VideoFormatEnum = VideoFormatEnum;
  @Input() creatorResult!: GoldSearchCreatorResult;
  @Input() directInvite: boolean = false;

  @ViewChild('bioPreview') bioPreview?: ElementRef<HTMLParagraphElement>;
  @ViewChild('bioMoreBtn') bioMoreBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('bioPopoverPanel') bioPopoverPanel?: ElementRef<HTMLElement>;
  @ViewChild('interestsMoreBtn') interestsMoreBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('interestsPopoverPanel') interestsPopoverPanel?: ElementRef<HTMLElement>;

  @Output() inviteNowOut: EventEmitter<string> = new EventEmitter<string>();

  today: Date = new Date();
  createdDaysAgo = signal<number>(0);
  hometown = signal<string>('');

  inviteSending = signal<boolean>(false);
  creatorAge = 0;
  totalFollowersDisplay = '';
  avgViewsDisplay = '';
  avgEngagementDisplay = '';

  readonly isBioPopoverOpen = computed(
    () => this.magicSearchService.openBioPopoverCreatorId() === this.creatorResult.creator.blazeFirebaseUid,
  );
  readonly isInterestsPopoverOpen = computed(
    () => this.magicSearchService.openInterestsPopoverCreatorId() === this.creatorResult.creator.blazeFirebaseUid,
  );
  bioPopoverStyle = signal<{ top: string; left: string } | null>(null);
  interestsPopoverStyle = signal<{ top: string; left: string } | null>(null);
  bioIsTruncated = signal(false);

  private bioPopoverDismissCleanup: (() => void) | null = null;
  private popoverDismissCleanup: (() => void) | null = null;

  private readonly syncBioPopoverDismissListeners = effect(() => {
    if (!this.isBioPopoverOpen()) {
      this.bioPopoverStyle.set(null);
      this.detachBioPopoverDismissListeners();
    }
  });

  private readonly syncInterestsPopoverDismissListeners = effect(() => {
    if (!this.isInterestsPopoverOpen()) {
      this.interestsPopoverStyle.set(null);
      this.detachPopoverDismissListeners();
    }
  });

  /** Services */
  public imageService = inject(ImageService);
  public formatService = inject(FormatService);
  private creatorService = inject(CreatorService);
  private miscService = inject(MiscService);
  public collaboratorsService = inject(CollaboratorService);
  private magicSearchService = inject(MagicSearchService);
  private briefApplicationService = inject(BriefApplicationService);
  private manageBriefTabsService = inject(ManageBriefTabsService);
  /** Services - END */

  ngOnInit(): void {
    this.createdDaysAgo.set(differenceInDays(this.today, this.creatorResult.creator.createdDate));
    this.hometown.set(this.miscService.getShortHometown(this.creatorResult.creator.homeTownName));
    this.creatorAge = this.formatService.calculateAge(this.creatorResult.creator.dateOfBirth);
    this.totalFollowersDisplay = this.formatService.convertIntToString(
      this.creatorResult.creator.tikTokFollowers
      + this.creatorResult.creator.instaFollowers
      + this.creatorResult.creator.snapchatFollowers
      + this.creatorResult.creator.youtubeSubscribers,
    );
    this.avgViewsDisplay = this.formatService.convertIntToString(this.getAverageViews());
    this.avgEngagementDisplay = this.getAvgEngaegement().toFixed(2);
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.measureBioTruncation());
  }

  ngOnDestroy(): void {
    this.detachBioPopoverDismissListeners();
    this.detachPopoverDismissListeners();
  }

  hasRealBio(): boolean {
    return (this.creatorResult.creator.bio?.trim().length ?? 0) > 0;
  }

  displayBio(): string {
    const bio = this.creatorResult.creator.bio?.trim();
    return bio && bio.length > 0 ? bio : 'Bio coming soon...';
  }

  private measureBioTruncation(): void {
    const element = this.bioPreview?.nativeElement;
    if (!element) {
      return;
    }

    this.bioIsTruncated.set(element.scrollHeight > element.clientHeight + 1);
  }

  toggleBioPopover(event: Event): void {
    event.stopPropagation();

    if (this.isBioPopoverOpen()) {
      this.closeBioPopover();
      return;
    }

    this.magicSearchService.openInterestsPopoverCreatorId.set(null);
    this.magicSearchService.openBioPopoverCreatorId.set(this.creatorResult.creator.blazeFirebaseUid);
    this.updateBioPopoverPosition();
    queueMicrotask(() => {
      this.updateBioPopoverPosition();
      this.attachBioPopoverDismissListeners();
    });
  }

  private updateBioPopoverPosition(): void {
    const button = this.bioMoreBtn?.nativeElement;
    if (!button) {
      return;
    }

    const rect = button.getBoundingClientRect();
    const popoverWidth = Math.min(BIO_POPOVER_MAX_WIDTH_PX, window.innerWidth - 16);
    const maxLeft = window.innerWidth - popoverWidth - 8;
    const left = Math.max(8, Math.min(rect.left, maxLeft));

    this.bioPopoverStyle.set({
      top: `${rect.bottom + 6}px`,
      left: `${left}px`,
    });
  }

  onBioPopoverPanelClick(event: Event): void {
    event.stopPropagation();
  }

  closeBioPopover(): void {
    if (!this.isBioPopoverOpen()) {
      return;
    }

    this.magicSearchService.openBioPopoverCreatorId.set(null);
    this.bioPopoverStyle.set(null);
    this.detachBioPopoverDismissListeners();
  }

  private attachBioPopoverDismissListeners(): void {
    this.detachBioPopoverDismissListeners();

    const onDocumentClick = () => this.closeBioPopover();
    const onDocumentKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.closeBioPopover();
      }
    };
    const onScroll = (event: Event) => {
      if (this.isScrollInsidePopover(event, this.bioPopoverPanel)) {
        return;
      }
      this.closeBioPopover();
    };

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);
    window.addEventListener('scroll', onScroll, true);

    this.bioPopoverDismissCleanup = () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onDocumentKeydown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }

  private detachBioPopoverDismissListeners(): void {
    this.bioPopoverDismissCleanup?.();
    this.bioPopoverDismissCleanup = null;
  }

  toggleInterestsPopover(event: Event): void {
    event.stopPropagation();

    if (this.isInterestsPopoverOpen()) {
      this.closeInterestsPopover();
      return;
    }

    this.magicSearchService.openBioPopoverCreatorId.set(null);
    this.magicSearchService.openInterestsPopoverCreatorId.set(this.creatorResult.creator.blazeFirebaseUid);
    this.updateInterestsPopoverPosition();
    queueMicrotask(() => {
      this.updateInterestsPopoverPosition();
      this.attachPopoverDismissListeners();
    });
  }

  private updateInterestsPopoverPosition(): void {
    const button = this.interestsMoreBtn?.nativeElement;
    if (!button) {
      return;
    }

    const rect = button.getBoundingClientRect();
    const popoverWidth = Math.min(384, window.innerWidth - 16);
    const maxLeft = window.innerWidth - popoverWidth - 8;
    const left = Math.max(8, Math.min(rect.left, maxLeft));

    this.interestsPopoverStyle.set({
      top: `${rect.bottom + 6}px`,
      left: `${left}px`,
    });
  }

  onInterestsPopoverPanelClick(event: Event): void {
    event.stopPropagation();
  }

  closeInterestsPopover(): void {
    if (!this.isInterestsPopoverOpen()) {
      return;
    }

    this.magicSearchService.openInterestsPopoverCreatorId.set(null);
    this.interestsPopoverStyle.set(null);
    this.detachPopoverDismissListeners();
  }

  private attachPopoverDismissListeners(): void {
    this.detachPopoverDismissListeners();

    const onDocumentClick = () => this.closeInterestsPopover();
    const onDocumentKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.closeInterestsPopover();
      }
    };
    const onScroll = (event: Event) => {
      if (this.isScrollInsidePopover(event, this.interestsPopoverPanel)) {
        return;
      }
      this.closeInterestsPopover();
    };

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);
    window.addEventListener('scroll', onScroll, true);

    this.popoverDismissCleanup = () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onDocumentKeydown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }

  private detachPopoverDismissListeners(): void {
    this.popoverDismissCleanup?.();
    this.popoverDismissCleanup = null;
  }

  private isScrollInsidePopover(event: Event, panel?: ElementRef<HTMLElement>): boolean {
    const target = event.target;
    const panelElement = panel?.nativeElement;
    return !!(panelElement && target instanceof Node && panelElement.contains(target));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if (changes) {
    //   if (changes['creator']) {
    //     this.createdDaysAgo.set(differenceInDays(this.today, this.creatorResult.creator.createdDate));
    //     this.hometown.set(this.miscService.getShortHometown(this.creatorResult.creator.homeTownName));
    //   }
    // }
  }

  getCompositeScorePpClass(compositeScore: number): string {
    if (compositeScore >= 0.7) {
      return `bg-gradient-to-br from-lime-500 to-green-500`;
    } else if (compositeScore >= 0.5) {
      return `bg-gradient-to-br from-teal-500 to-blue-500`;
    } else if (compositeScore >= 0.3) {
      return `bg-gradient-to-br from-brand-studio to-brand-purple`;
    } else {
      return `bg-gradient-to-br from-brand-agency-dark to-brand-purple`;
    }
  }

  getMatchWord(compositeScore: number): string {
    if (compositeScore >= 0.7) {
      return `Match`;
    } else if (compositeScore >= 0.5) {
      return `Good Match`;
    } else if (compositeScore >= 0.3) {
      return `Fair Match`;
    } else {
      return `Poor Match`;
    }
  }

  getInterestText(interestId: string | number): string {
    const id = String(interestId);

    if (!/^\d+$/.test(id)) {
      return id;
    }

    const fromApi = this.creatorService.shoutCreatorInterests?.find(
      (interest) => String(interest.id) === id,
    );
    if (fromApi?.tagName) {
      return fromApi.tagName;
    }

    return interestsList.find((interest) => String(interest.id) === id)?.name ?? '';
  }

  getAllInterestLabels(): string[] {
    const interests = this.creatorResult.creator.interests;
    if (!interests?.length) {
      return [];
    }

    return interests
      .map((interest: string | number) => this.getInterestText(interest))
      .filter((label: string) => label.length > 0 && label !== 'Loading...');
  }

  getAverageViews() {
    if (this.creatorResult.creator && this.creatorResult.creator.ssCreator) {
      if (this.creatorResult.creator.ssCreator.instagramAverages && this.creatorResult.creator.ssCreator.tiktokAverages) {
        return ((this.creatorResult.creator.ssCreator.instagramAverages?.averageViews || 0) + (this.creatorResult.creator.ssCreator.tiktokAverages?.averageViews || 0)) / 2
      } else if (this.creatorResult.creator.ssCreator.instagramAverages) {
        return (this.creatorResult.creator.ssCreator.instagramAverages?.averageViews || 0)
      } else if (this.creatorResult.creator.ssCreator.tiktokAverages) {
        return (this.creatorResult.creator.ssCreator.tiktokAverages?.averageViews || 0)
      } else {
        return 0
      }
    } else {
      return 0
    }
  }

  getAvgEngaegement() {
    if (this.creatorResult.creator && this.creatorResult.creator.ssCreator) {
      if (this.creatorResult.creator.ssCreator.instagramAverages && this.creatorResult.creator.ssCreator.tiktokAverages) {
        return ((this.creatorResult.creator.ssCreator.instagramAverages?.averageEngagementRate || 0) + (this.creatorResult.creator.ssCreator.tiktokAverages?.averageEngagementRate || 0)) / 2
      } else if (this.creatorResult.creator.ssCreator.instagramAverages) {
        return (this.creatorResult.creator.ssCreator.instagramAverages?.averageEngagementRate || 0);
      } else if (this.creatorResult.creator.ssCreator.tiktokAverages) {
        return (this.creatorResult.creator.ssCreator.tiktokAverages?.averageEngagementRate || 0);
      } else {
        return 0;
      }
    } else {
      return 0;
    }
  }

  get scoreBreakdownMetrics(): ScoreBreakdownMetric[] {
    if (!this.creatorResult || !this.creatorResult.scoreBreakdown) {
      return [];
    }

    const breakdown = this.creatorResult.scoreBreakdown;

    const metrics: ScoreBreakdownMetric[] = [
      { key: 'recommendation', label: 'Recommended', value: breakdown.recommendation || 0, percent: 0 },
      // { key: 'rating', label: 'Rating Quality', value: breakdown.rating || 0, valueLabelPipe: '1.0-0', percent: 0 },
      { key: 'speed', label: '1st Draft Speed', value: breakdown.speed || 0, percent: 0 },
      // { key: 'reviewCount', label: 'Review Volume', value: breakdown.reviewCount || 0, percent: 0 },
      { key: 'recency', label: 'How active?', value: breakdown.recency || 0, percent: 0 },
      { key: 'vectorRelevance', label: 'Content Relevance', value: breakdown.vectorRelevance || 0, percent: 0 },
    ];

    const maxValue = Math.max(...metrics.map(metric => metric.value), 1);

    return metrics.map(metric => ({
      ...metric,
      percent: Math.max(0, (metric.value / maxValue) * 100),
    }));
  }

  launchPortfolio() {
    this.magicSearchService.portfolioCreatorId.set(this.creatorResult.creator.blazeFirebaseUid);
    this.magicSearchService.portfolioCreator.set(this.creatorResult.creator);
  }

  inviteNow() {
    console.log('inviteNow(): ', this.creatorResult.creator.blazeFirebaseUid);
    this.inviteSending.set(true);
    const brief = this.manageBriefTabsService.focussedBrief();
    if (!brief) {
      this.inviteSending.set(false);
      return;
    }
    this.briefApplicationService.createBriefApplication(this.creatorResult.creator, brief).subscribe({
      next: (response) => {
        console.log('response: ', response);
        this.creatorResult.briefApplication = response;
        this.inviteSending.set(false);
      },
      error: (error) => {
        console.log('error: ', error);
        this.inviteSending.set(false);
      }
    });
  }

  get matchedPreviewVideos(): GoldSearchMatchedVideo[] {
    return this.creatorResult?.evidence?.matchedVideos?.slice(0, 3) ?? [];
  }

  previewVideo(videoFile: GoldSearchMatchedVideo['videoFile']) {
    this.magicSearchService.previewVideo.set(videoFile as unknown as VideoResponseDto);
  }
}

interface GoldSearchMatchedVideo {
  videoFile: {
    id: string;
    s3_url: string;
    cover?: string;
  };
  score: number;
  source: string;
}

interface ScoreBreakdownMetric {
  key: string;
  label: string;
  value: number;
  percent: number;
  valueLabelPipe?: string;
}
